import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { AsyncSubject, Observable, partition } from 'rxjs';
import { delayWhen, filter, finalize, first, repeatWhen, takeUntil } from 'rxjs/operators';

import {
  AuthUsecase,
  DistinctSubject,
  Period,
  PeriodSubject,
  UserTimezone,
  WebSocketUsecase,
  recursiveQuery,
} from '@daikin-tic/dxone-com-lib';

import { InspectionResult, InspectionResultQueryParams, InspectionResultUpdateParams } from '../models/inspection-result.model';
import { InspectionResultArchiveGateway } from '../usecases/inspection-result-archive.gateway';
import { InspectionResultArchiveUsecase } from '../usecases/inspection-result-archive.usecase';

@Injectable()
export class InspectionResultArchiveInteractor extends InspectionResultArchiveUsecase {
  get inspectionResults$(): Observable<InspectionResult[]> {
    return this._inspectionResults;
  }
  get period$(): Observable<Period> {
    return this._period;
  }

  private readonly _inspectionResults = new DistinctSubject<InspectionResult[]>([]);
  private readonly _period = new PeriodSubject({ from: -1, to: -1 });

  constructor(
    private _authUsecase: AuthUsecase,
    private _webSocketUsecase: WebSocketUsecase,
    private _inspectionResultArchiveGateway: InspectionResultArchiveGateway,
  ) {
    super();
    this._authUsecase.authState$.pipe(first(({ status }) => status === 'signedIn')).subscribe(() => {
      this._period.now();
    });
    const [open$, close$] = partition(this._webSocketUsecase.isOpen$, isOpen => isOpen);
    this.period$
      .pipe(
        filter(({ from, to }) => from >= 0 && to >= 0),
        takeUntil(close$),
        finalize(() => this._inspectionResults.next([])),
        repeatWhen(notifications => notifications.pipe(delayWhen(() => open$))),
      )
      .subscribe(({ from, to }) => {
        const queryParams: InspectionResultQueryParams = { createdFrom: from.toString(), createdTo: to.toString() };
        recursiveQuery(params => this._inspectionResultArchiveGateway.listInspectionResults(params), queryParams).subscribe(
          inspectionResults => this._inspectionResults.next(inspectionResults),
        );
      });
  }

  changeTimezone(timezone: UserTimezone): void {
    this._period.tz(timezone);
  }

  changePeriod(period: Period): void {
    this._period.next(period);
  }

  reload(): void {
    const now = dayjs().tz();
    if (this._period.includes(now.startOf('day').unix())) {
      this._period.next({ from: this._period.value.from, to: now.unix() });
    }
  }

  getInspectionResult(inspectionId: string): Observable<string> {
    const result = new AsyncSubject<string>();
    this._inspectionResultArchiveGateway.getInspectionResult(inspectionId).subscribe({
      next: ({ url }) => result.next(url),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  updateInspectionResult(inspectionId: string, params: InspectionResultUpdateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._inspectionResultArchiveGateway.updateInspectionResult(inspectionId, params).subscribe({
      next: updatedInspection => this.replaceInspectionResult(updatedInspection),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  private replaceInspectionResult(inspectionResult: InspectionResult): void {
    const inspectionResults = this._inspectionResults.value;
    const index = inspectionResults.findIndex(({ inspectionId }) => inspectionId === inspectionResult.inspectionId);
    if (index < 0) {
      return;
    }
    inspectionResults[index] = inspectionResult;
    this._inspectionResults.next([...inspectionResults]);
  }
}
